import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import EntityTable from '../components/Listing/EntityTable';
import InputText from '../components/Form/Fields/InputText';
import Select from '../components/Form/Fields/Select';
import { getYesNoOptions } from '../config/YesNo';
import SendExchangeModal from '../components/Modal/SendExchangeModal';
import ReceiveExchangeModal from '../components/Modal/ReceiveExchangeModal';
import PharmacyGardeModal from '../components/Modal/PharmacyGardeModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faArrowLeft, faEllipsisH, faArrowUp, faArrowDown, faPhone, faClock } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../api';

export default function Pharmacies() {
    const { t } = useTranslation();
    const { setTitle, setTitleIcon } = usePageTitle();
    const navigate = useNavigate();
    const tableRef = useRef(null);
    const [sendTarget, setSendTarget] = useState(null);
    const [receiveTarget, setReceiveTarget] = useState(null);
    const [gardeTarget, setGardeTarget] = useState(null);

    useEffect(() => {
        setTitle(`${t('Confrères')}<span style="display:block;font-size:11px;font-weight:400;color:#9ca3af;margin-top:5px;">${t('pharmacies.subtitle')}</span>`);
        document.title = t('Confrères');
        setTitleIcon('ti-building-store');
        return () => setTitleIcon(null);
    }, [setTitle, setTitleIcon, t]);

    useBreadcrumbSetup([{ label: t('Confrères'), url: '/confreres' }]);

    const openDetail = (row) => navigate(`/confreres/${row.id}`);

    // Pharmacy names come from imported data in inconsistent casing (some fully
    // uppercase, some already proper case) — normalize to title case for display
    // so the listing reads consistently, without touching the underlying data.
    const toTitleCase = (str = '') =>
        str.toLowerCase().replace(/(^|[\s'-])\p{L}/gu, (c) => c.toUpperCase());

    const pharmacyInitials = (name = '') => {
        const words = name.trim().split(/\s+/).filter(Boolean);
        if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
        return name.slice(0, 2).toUpperCase();
    };

    const stringToColor = (str) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
        let color = '#';
        for (let i = 0; i < 3; i++) color += (`00${((hash >> (i * 8)) & 0xFF).toString(16)}`).slice(-2);
        return color;
    };

    const formatGardeDate = (value) =>
        new Date(value).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    const formatGardeTooltip = (row) => {
        if (row.gardeActive) {
            return row.gardeEnd
                ? t('pharmacies.garde_tooltip.until_date', { date: formatGardeDate(row.gardeEnd) })
                : t('pharmacies.garde_tooltip.active');
        }
        if (row.gardeStart && row.gardeEnd) {
            return t('pharmacies.garde_tooltip.scheduled_range', {
                start: formatGardeDate(row.gardeStart),
                end: formatGardeDate(row.gardeEnd),
            });
        }
        return t('pharmacies.garde_tooltip.scheduled');
    };

    const formatOwnerName = (name = '') => {
        const words = name.trim().split(/\s+/).filter(Boolean);
        if (words.length < 2) return name;
        return `${words[0]} ${words[1][0]}.`;
    };

    const columns = [
        {
            Header: '#',
            accessor: 'id',
            sortable: true,
            className: 'text-center',
            filterable: { component: InputText },
            Cell: (row) => <span style={{ fontSize: 14, fontWeight: 500, color: '#9ca3af' }}>{row.id}</span>,
        },
        {
            Header: t('Pharmacy'),
            accessor: 'name',
            sortable: true,
            minWidth: '260px',
            filterable: { component: InputText },
            Cell: (row) => {
                const bg = stringToColor(row.name || '');
                return (
                    <div className="d-flex align-items-center gap-3">
                        <div style={{ padding: 2, width: 38, height: 38, border: '1px solid #e5e7eb', borderRadius: '50%', background: '#f9fafb', flexShrink: 0 }}>
                            <div style={{
                                width: '100%', height: '100%', borderRadius: '50%',
                                background: bg, color: '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 700, fontSize: 13, letterSpacing: '0.03em',
                            }}>
                                {pharmacyInitials(row.name)}
                            </div>
                        </div>
                        <div style={{ minWidth: 0, maxWidth: 260 }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#111827' }}>
                                {toTitleCase(row.name)}
                                {row.onGarde && (
                                    <FontAwesomeIcon
                                        icon={faClock}
                                        style={{ color: '#f59e0b', fontSize: 12, flexShrink: 0 }}
                                        data-tooltip-id="tooltip"
                                        data-tooltip-content={formatGardeTooltip(row)}
                                    />
                                )}
                            </span>
                            {row.address && (
                                <span
                                    style={{ display: 'block', fontSize: 12, color: '#9ca3af', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    data-tooltip-id="tooltip"
                                    data-tooltip-content={row.address}
                                >
                                    {row.address}
                                </span>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            Header: t('pharmacies.column.owner'),
            accessor: 'ownerName',
            sortable: true,
            filterable: { component: InputText },
            Cell: (row) => row.ownerName
                ? (
                    <span
                        style={{ fontSize: 13, color: '#374151' }}
                        data-tooltip-id="tooltip"
                        data-tooltip-content={row.ownerName}
                    >
                        {formatOwnerName(row.ownerName)}
                    </span>
                )
                : <span className="cell-muted">—</span>,
        },
        {
            Header: t('pharmacies.column.phone'),
            accessor: 'telephone',
            filterable: { component: InputText },
            Cell: (row) => row.telephone
                ? (
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: '#f3f4f6', color: '#374151',
                        padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                    }}>
                        <FontAwesomeIcon icon={faPhone} style={{ fontSize: 10 }} />
                        {row.telephone}
                    </span>
                )
                : <span className="cell-muted">—</span>,
        },
        {
            Header: t('Email'),
            accessor: 'email',
            visible: false,
            filterable: { component: InputText },
            Cell: (row) => row.email
                ? <span style={{ fontSize: 13, color: '#374151' }}>{row.email}</span>
                : <span className="cell-muted">—</span>,
        },
        {
            Header: t('pharmacies.column.address'),
            accessor: 'address',
            visible: false,
            filterable: { component: InputText },
            Cell: (row) => row.address
                ? <span style={{ fontSize: 13, color: '#374151' }}>{row.address}</span>
                : <span className="cell-muted">—</span>,
        },
        {
            Header: t('pharmacies.column.sent'),
            accessor: 'sentCount',
            className: 'text-center',
            Cell: (row) => (
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: row.sentCount > 0 ? '#eff6ff' : '#f3f4f6',
                    color: row.sentCount > 0 ? '#1d4ed8' : '#9ca3af',
                    padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700,
                }}>
                    <FontAwesomeIcon icon={faArrowUp} style={{ fontSize: 10 }} />
                    {row.sentCount ?? 0}
                </span>
            ),
        },
        {
            Header: t('pharmacies.column.received'),
            accessor: 'receivedCount',
            className: 'text-center',
            Cell: (row) => (
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: row.receivedCount > 0 ? '#ecfdf5' : '#f3f4f6',
                    color: row.receivedCount > 0 ? '#059669' : '#9ca3af',
                    padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700,
                }}>
                    <FontAwesomeIcon icon={faArrowDown} style={{ fontSize: 10 }} />
                    {row.receivedCount ?? 0}
                </span>
            ),
        },
        {
            Header: t('pharmacies.column.city'),
            accessor: 'city',
            sortable: true,
            filterable: { component: InputText },
            Cell: (row) => row.city
                ? <span style={{ fontSize: 13, color: '#374151' }}>{row.city}</span>
                : <span className="cell-muted">—</span>,
        },
        {
            accessor: 'onGarde',
            visible: false,
            filterable: { component: Select, options: getYesNoOptions(), label: t('pharmacies.column.on_garde') },
        },
        {
            Header: '',
            accessor: '_actions',
            className: 'col-actions',
            Cell: (row) => (
                <div className="cell-dropdown">
                    <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                        <li>
                            <button
                                className="dropdown-item"
                                onClick={(e) => { e.stopPropagation(); setSendTarget(row); }}
                            >
                                <FontAwesomeIcon icon={faPaperPlane} className="me-2 text-primary" />
                                {t('pharmacies.action.send')}
                            </button>
                        </li>
                        <li>
                            <button
                                className="dropdown-item"
                                onClick={(e) => { e.stopPropagation(); setReceiveTarget(row); }}
                            >
                                <FontAwesomeIcon icon={faArrowLeft} className="me-2 text-primary" />
                                {t('pharmacies.action.receive')}
                            </button>
                        </li>
                        <li>
                            <button
                                className="dropdown-item"
                                onClick={(e) => { e.stopPropagation(); setGardeTarget(row); }}
                            >
                                <FontAwesomeIcon icon={faClock} className="me-2 text-primary" />
                                {t('pharmacies.action.set_garde')}
                            </button>
                        </li>
                    </ul>
                </div>
            ),
        },
    ];

    // Card-view design for this listing (Table's enableCardView feature, forced on mobile)
    // — reuses the actions column's own dropdown so both views offer identical actions.
    const actionsColumn = columns.find((c) => c.accessor === '_actions');
    const renderPharmacyCard = (row) => {
        const bg = stringToColor(row.name || '');
        return (
            <div className="card h-100 shadow-sm" style={{ cursor: 'pointer' }} onClick={() => openDetail(row)}>
                <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
                    <div className="d-flex align-items-center gap-3">
                        <div style={{
                            width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                            background: bg, color: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 700, fontSize: 13, letterSpacing: '0.03em',
                        }}>
                            {pharmacyInitials(row.name)}
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13.5, color: '#111827' }}>
                                {toTitleCase(row.name)}
                                {row.onGarde && (
                                    <FontAwesomeIcon icon={faClock} style={{ color: '#f59e0b', fontSize: 11, flexShrink: 0 }} />
                                )}
                            </span>
                            {row.address && (
                                <span style={{ fontSize: 11.5, color: '#9ca3af', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {row.address}
                                </span>
                            )}
                        </div>
                    </div>

                    {row.ownerName && <span style={{ fontSize: 11.5, color: '#6b7280' }}>{formatOwnerName(row.ownerName)}</span>}
                    {row.telephone && (
                        <span style={{ fontSize: 11.5, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 5 }}>
                            <FontAwesomeIcon icon={faPhone} style={{ fontSize: 10, color: '#9ca3af' }} />
                            {row.telephone}
                        </span>
                    )}

                    <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 'auto', paddingTop: 6 }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                background: row.sentCount > 0 ? '#eff6ff' : '#f3f4f6',
                                color: row.sentCount > 0 ? '#1d4ed8' : '#9ca3af',
                                padding: '3px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                            }}>
                                <FontAwesomeIcon icon={faArrowUp} style={{ fontSize: 9 }} />
                                {row.sentCount ?? 0}
                            </span>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                background: row.receivedCount > 0 ? '#ecfdf5' : '#f3f4f6',
                                color: row.receivedCount > 0 ? '#059669' : '#9ca3af',
                                padding: '3px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                            }}>
                                <FontAwesomeIcon icon={faArrowDown} style={{ fontSize: 9 }} />
                                {row.receivedCount ?? 0}
                            </span>
                        </div>
                        <div onClick={(e) => e.stopPropagation()}>
                            {actionsColumn?.Cell(row)}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <EntityTable
                ref={tableRef}
                endpoint={`${API_BASE_URL}/api/v1/pharmacies`}
                columns={columns}
                defaultSort="exchanges"
                defaultDirection="asc"
                pageSize={20}
                emptyMessage={t('pharmacies.empty')}
                searchable={true}
                multiSelect={true}
                searchPlaceholder={t('pharmacies.search_placeholder')}
                onRowClick={(row) => openDetail(row)}
                enableCardView={true}
                renderCard={renderPharmacyCard}
            />

            {/* Modal envoi */}
            <SendExchangeModal
                show={!!sendTarget}
                onClose={() => setSendTarget(null)}
                targetPharmacy={sendTarget}
            />

            {/* Modal récupération */}
            <ReceiveExchangeModal
                show={!!receiveTarget}
                onClose={() => setReceiveTarget(null)}
                sourcePharmacy={receiveTarget}
            />

            {/* Modal garde */}
            <PharmacyGardeModal
                show={!!gardeTarget}
                onClose={() => setGardeTarget(null)}
                onSaved={() => tableRef.current?.reload()}
                pharmacy={gardeTarget}
            />
        </>
    );
}
